import { api } from './axios';
import { authService } from './auth';
import type { Sistema, CreateSistemaRequest } from '@/models';

interface ItemSistemaPayload {
  nome: string;
  descricao?: string;
  sistema_fk: number;
}

interface ItemSistemaUpdatePayload {
  nome: string;
  descricao?: string;
}

interface SistemasFilters {
  buscar?: string;
  categoria?: string;
}

class SistemasService {
  /**
   * Listar todos os sistemas
   */
  async getAll(filters: SistemasFilters = {}): Promise<Sistema[]> {
    await authService.ensureSanctumInitialized();
    
    const params = new URLSearchParams();
    if (filters.buscar) params.append('buscar', filters.buscar);
    if (filters.categoria) params.append('categoria', filters.categoria);
    
    const response = await api.get<Sistema[]>(`/sistemas?${params.toString()}`);
    return response.data;
  }

  /**
   * Obter sistema por ID
   */
  async getById(sistemaId: number): Promise<Sistema> {
    await authService.ensureSanctumInitialized();
    
    const response = await api.get<Sistema>(`/sistemas/${sistemaId}`);
    return response.data;
  }

  /**
   * Criar um novo sistema
   */
  async create(data: CreateSistemaRequest | FormData): Promise<Sistema> {
    await authService.ensureSanctumInitialized();
    
    const config = data instanceof FormData 
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : undefined;
    
    const response = await api.post<Sistema>('/sistemas', data, config);
    return response.data;
  }

  /**
   * Atualizar um sistema existente
   */
  async update(id: number, data: Partial<CreateSistemaRequest> | FormData): Promise<Sistema> {
    await authService.ensureSanctumInitialized();
    
    const config = data instanceof FormData 
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : undefined;
    
    const response = await api.put<Sistema>(`/sistemas/${id}`, data, config);
    return response.data;
  }

  /**
   * Deletar um sistema (soft delete)
   */
  async delete(id: number): Promise<void> {
    await authService.ensureSanctumInitialized();
    
    await api.delete(`/sistemas/${id}`);
  }

  async createModulo(data: ItemSistemaPayload): Promise<void> {
    await authService.ensureSanctumInitialized();
    await api.post('/sistemas/modulos', data);
  }

  async updateModulo(id: number, data: ItemSistemaUpdatePayload): Promise<void> {
    await authService.ensureSanctumInitialized();
    await api.put(`/sistemas/modulos/${id}`, data);
  }

  async deleteModulo(id: number): Promise<void> {
    await authService.ensureSanctumInitialized();
    await api.delete(`/sistemas/modulos/${id}`);
  }

  async createPerfil(data: ItemSistemaPayload): Promise<void> {
    await authService.ensureSanctumInitialized();
    await api.post('/sistemas/perfis', data);
  }

  async updatePerfil(id: number, data: ItemSistemaUpdatePayload): Promise<void> {
    await authService.ensureSanctumInitialized();
    await api.put(`/sistemas/perfis/${id}`, data);
  }

  async deletePerfil(id: number): Promise<void> {
    await authService.ensureSanctumInitialized();
    await api.delete(`/sistemas/perfis/${id}`);
  }
}

export const sistemasService = new SistemasService();
